import {
  Brand,
  getProductReplacements,
  type ReplacementProduct
} from '@fe-sdk/cbx-products'
import {
  groupBy as cbxGroupBy,
  productMapper as cbxProductMapper,
  sortItemsBySequenceNumber,
  sortVariantsBySequenceNumber
} from '.'
import { type ProductDataVariant } from '../../../../services/product/product-data'
import { StockLevelStatus } from '../../../../services/stock'
import { type GroupedItem } from '../models'
import { AkamaiApiHeaders } from '../../../../utils/api-headers'

type ReplacementsGroupBy = FetchReplacementsDataParams['groupBy']

const isL1InStock = (l1?: ProductDataVariant): boolean =>
  l1?.stock?.stockLevelStatus !== StockLevelStatus.outOfStock

/**
 * Builds an effective groupBy for the out-of-stock case: L1's global article
 * code is added to the `except` list so it lands in its own product card,
 * while the L2 color variants group together. Any authored `except` entries
 * are preserved and merged (deduped).
 */
const buildEffectiveGroupBy = (
  l1Code: string,
  groupBy?: ReplacementsGroupBy
): ReplacementsGroupBy => ({
  productGroups: groupBy?.productGroups ?? [],
  except: Array.from(new Set([...(groupBy?.except ?? []), l1Code]))
})

/**
 * Moves the card containing the given L1 global article code to the front of
 * the list while preserving the relative order of the remaining cards
 * (stable). Returns the list unchanged when no card matches.
 */
const moveL1CardFirst = (
  items: GroupedItem[],
  l1Code: string
): GroupedItem[] => {
  const l1Index = items.findIndex((item) =>
    item.variants.some((variant) => variant.globalArticleCode === l1Code)
  )

  if (l1Index <= 0) {
    return items
  }

  const reordered = items.slice()
  const [l1Card] = reordered.splice(l1Index, 1)
  reordered.unshift(l1Card)
  return reordered
}

const groupAndSort = (
  itemsForGroupBy: ProductDataVariant[],
  groupBy?: ReplacementsGroupBy
): GroupedItem[] =>
  Object.values(cbxGroupBy(itemsForGroupBy, cbxProductMapper, groupBy))
    .map((item: GroupedItem) => ({
      ...item,
      variants: item.variants.slice().sort(sortVariantsBySequenceNumber)
    }))
    .slice()
    .sort(sortItemsBySequenceNumber)

const toProductDataVariant = (item: ReplacementProduct): ProductDataVariant => {
  const assets = item.assets?.map((asset) => ({
    assetFullUrl: asset?.assetFullUrl ?? '',
    assetRelativePath: asset?.assetRelativePath ?? '',
    assetType: asset?.assetType ?? ''
  }))

  return {
    globalArticleCode: item.globalArticleCode ?? '',
    globalArticleName: item.globalArticleName ?? '',
    productFamily: item.productFamily ?? '',
    brand: item.brand ?? '',
    baseArticle: {
      baseArticleCode: item.baseArticle?.baseArticleCode ?? '',
      baseModelName: item.baseArticle?.baseModelName ?? ''
    },
    nuance: {
      code: item.color?.code ?? '',
      description: item.color?.description ?? '',
      group: {
        hexCode: item.color?.group?.hexCode ?? '',
        code: item.color?.group?.code ?? '',
        name: item.color?.group?.name ?? ''
      },
      hexCode: item.color?.hexCode ?? '',
      name: item.color?.name ?? ''
    },
    price: {
      grossPrice: item?.price?.grossPrice ?? item?.price?.value ?? 0,
      priceCurrency: item?.price?.priceCurrency ?? '',
      priceChannel: item?.price?.priceChannel ?? '',
      isServiceFeeIncluded: item?.price?.isServiceFeeIncluded ?? false,
      ...(typeof item?.price?.subscriptionPrice === 'number'
        ? { subscriptionPrice: item?.price.subscriptionPrice }
        : {}),
      ...(item?.price?.priceDiscount
        ? { priceDiscount: item?.price?.priceDiscount }
        : {})
    },
    assets,
    seo: item.seo,
    sequenceNumber: item.sequenceNumber,
    stock: {
      stockLevel: item.stock?.stockLevel ?? 0,
      stockLevelStatus: item.stock?.stockLevelStatus ?? ''
    }
  }
}

type ReplacementsConfig = {
  market: string
  channel: string
  xChannel: string
  loggerEnabled: boolean
  locale: string
  cbxUrl: string
  brand?: Brand
  touchpoint?: string
}

interface FetchReplacementsDataParams extends AkamaiApiHeaders {
  config: ReplacementsConfig
  productId: string
  groupBy?: {
    productGroups: {
      productFamily: string
      productProperty: string
    }[]
    except: string[]
  }
}

export const fetchReplacementsData = async ({
  config,
  productId,
  groupBy,
  customHeaders
}: FetchReplacementsDataParams) => {
  const sdkResponse = await getProductReplacements({
    productId,
    baseURL: config?.cbxUrl ?? '',
    brand: config?.brand ?? 'IQOS',
    channel:
      config?.channel?.toLowerCase() === 'web'
        ? 'web'
        : (config?.channel ?? ''),
    xChannel: config?.xChannel || 'WEBSITE',
    countryCode: config?.market?.toUpperCase() ?? '',
    market: config?.market ?? '',
    locale: config?.locale,
    touchpoint: config?.touchpoint,
    currentPage: 1,
    pageSize: 100,
    forwardedHost: 'default',
    ...(customHeaders ? { customHeaders } : {})
  })

  if ('error' in sdkResponse || !sdkResponse.data) {
    return {
      data: {
        gcodes: [] as string[],
        facets: [],
        items: [] as GroupedItem[]
      }
    }
  }

  const replacements = sdkResponse.data.items ?? []

  const gcodes = replacements.map(({ globalArticleCode }) => globalArticleCode)

  const itemsForGroupBy = replacements.map(toProductDataVariant)

  // On the replacement PLP the consumer is replacing an exact product (L1).
  // The L1 variant is the replacement whose global article code matches the
  // requested productId. L2 products are the other color variants of the same
  // product and are only surfaced when L1 is out of stock.
  const l1Variant = itemsForGroupBy.find(
    (variant) => variant.globalArticleCode === productId
  )

  let items: GroupedItem[]

  if (!l1Variant) {
    // L1 not found among replacements: fall back to grouping everything with
    // the authored groupBy unchanged.
    items = groupAndSort(itemsForGroupBy, groupBy)
  } else if (isL1InStock(l1Variant)) {
    // L1 has stock: show only the exact product being replaced (a single card).
    items = groupAndSort([l1Variant], groupBy)
  } else {
    // L1 is out of stock: show L1 in its own card (via the `except` mechanism)
    // and the L2 color variants grouped into another card.
    const l1Code = l1Variant.globalArticleCode ?? ''
    const effectiveGroupBy = buildEffectiveGroupBy(l1Code, groupBy)
    // The L1 card must always appear first, regardless of sequence numbers.
    items = moveL1CardFirst(
      groupAndSort(itemsForGroupBy, effectiveGroupBy),
      l1Code
    )
  }

  return {
    data: {
      gcodes,
      facets: [],
      items
    }
  }
}
